import {
  CaptureUpdateAction,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { supportsAnchorPick } from "./presets";
import { getElementAnimations, withElementAnimations } from "./types";

import type { PathDrawTarget } from "./pathDraw";

// pivot params are normalized bbox coords; the schema allows one bbox beyond
// each edge (orbit around a nearby point) — keep writes inside those bounds
// or validateParamBounds drops the whole clip on reload
const PIVOT_MIN = -1;
const PIVOT_MAX = 2;

const clampPivot = (value: number) =>
  Math.round(Math.min(PIVOT_MAX, Math.max(PIVOT_MIN, value)) * 100) / 100;

/** Converts a scene point to the element's normalized pivot coords —
 * unrotating the point about the element center first, since the renderer's
 * animationPivot works in the unrotated bbox space while the element itself
 * may be drawn rotated. */
export const scenePointToPivot = (
  element: {
    x: number;
    y: number;
    width: number;
    height: number;
    angle: number;
  },
  sceneX: number,
  sceneY: number,
): { pivotX: number; pivotY: number } => {
  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const cos = Math.cos(-element.angle);
  const sin = Math.sin(-element.angle);
  const dx = sceneX - cx;
  const dy = sceneY - cy;
  const lx = cx + dx * cos - dy * sin;
  const ly = cy + dx * sin + dy * cos;
  return {
    // zero-sized axes (straight lines) have no bbox extent — anchor center
    pivotX: element.width ? clampPivot((lx - element.x) / element.width) : 0.5,
    pivotY: element.height
      ? clampPivot((ly - element.y) / element.height)
      : 0.5,
  };
};

/** Inverse of scenePointToPivot: where a clip's normalized pivot sits in
 * scene space (re-applying the element's own rotation about its center) —
 * used to position the draggable anchor handle. */
export const pivotScenePoint = (
  element: {
    x: number;
    y: number;
    width: number;
    height: number;
    angle: number;
  },
  pivotX: number,
  pivotY: number,
): { x: number; y: number } => {
  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const lx = element.x + element.width * pivotX;
  const ly = element.y + element.height * pivotY;
  const cos = Math.cos(element.angle);
  const sin = Math.sin(element.angle);
  const dx = lx - cx;
  const dy = ly - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
};

/** Writes the picked scene point into each target clip's pivot params and
 * captures ONE undo entry. Clips without pivot params (removed / switched
 * preset while picking) are skipped. */
export const bindAnchorToClips = (
  api: Pick<
    ExcalidrawImperativeAPI,
    | "getSceneElements"
    | "getSceneElementsIncludingDeleted"
    | "mutateElement"
    | "updateScene"
  >,
  targets: readonly PathDrawTarget[],
  sceneX: number,
  sceneY: number,
) => {
  const byId = new Map(api.getSceneElements().map((el) => [el.id, el]));
  let bound = 0;
  for (const { elementId, index } of targets) {
    const el = byId.get(elementId);
    if (!el) {
      continue;
    }
    const clips = getElementAnimations(el);
    if (!clips[index] || !supportsAnchorPick(clips[index].preset)) {
      continue;
    }
    const pivot = scenePointToPivot(el, sceneX, sceneY);
    const next = clips.map((clip, i) =>
      i === index ? { ...clip, params: { ...clip.params, ...pivot } } : clip,
    );
    api.mutateElement(el, { customData: withElementAnimations(el, next) });
    bound++;
  }
  if (bound > 0) {
    api.updateScene({
      elements: api.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  }
  return bound;
};

// ponytail: module-level singleton, same shape as pathDraw's — one pick
// session at a time is also the correct UX
let cancelActive: (() => void) | null = null;

/**
 * "Pick anchor on canvas" mode: the next canvas click becomes the rotation
 * anchor of the target clips (normalized against each element's bbox).
 * Clicking outside the canvas or pressing Esc cancels.
 */
export const beginPickAnchor = (
  api: ExcalidrawImperativeAPI,
  targets: readonly PathDrawTarget[],
) => {
  cancelActive?.();
  if (targets.length === 0) {
    return;
  }
  api.setToast({
    message: "Click a point on the canvas to set the anchor — Esc to cancel",
    duration: 10000,
  });
  const finish = () => {
    cancelActive = null;
    window.removeEventListener("pointerdown", onPointerDown, true);
    window.removeEventListener("keydown", onKeyDown, true);
  };
  const onPointerDown = (event: PointerEvent) => {
    // capture-phase so the click never reaches the canvas (no selection
    // change); a click anywhere but the canvas cancels the pick
    if (!(event.target instanceof HTMLCanvasElement)) {
      finish();
      api.setToast(null);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    finish();
    const { x, y } = viewportCoordsToSceneCoords(
      { clientX: event.clientX, clientY: event.clientY },
      api.getAppState(),
    );
    const bound = bindAnchorToClips(api, targets, x, y);
    api.setToast(bound > 0 ? { message: "Anchor set", duration: 2000 } : null);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      finish();
      api.setToast(null);
    }
  };
  window.addEventListener("pointerdown", onPointerDown, true);
  window.addEventListener("keydown", onKeyDown, true);
  cancelActive = finish;
};
